import React, {
  ChangeEvent,
  CSSProperties,
  FC,
  HTMLAttributes,
  Ref,
  useEffect,
  useState,
} from 'react';
import clsx from 'clsx';
import { useTheme } from '../../../hooks/useTheme';
import Label, { LabelAnimateState, LabelVerticalAlign } from '../Label/Label';
import styles from './TextInput.module.css';

export enum SupportedInputType {
  text = 'text',
  password = 'password',
  email = 'email',
  number = 'number',
  tel = 'tel',
  search = 'search',
}

export interface TextInputProps {
  id?: string;
  name?: string;
  title?: string;
  type?: SupportedInputType;
  disabled?: boolean;
  centered?: boolean;
  animateLabel?: boolean;
  autoComplete?: string;
  placeholder?: string;
  label?: string;
  value?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  minLength?: number;
  maxLength?: number;
  error?: boolean;
  containerOverridesStyle?: CSSProperties;
  className?: string;
  inputRef?: Ref<HTMLInputElement>;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  ariaLabelledby?: string;
  ariaDescribedBy?: string;
}

const TextInput: FC<TextInputProps> = ({
  id,
  centered = false,
  animateLabel = false,
  label,
  name,
  title,
  type = SupportedInputType.text,
  disabled = false,
  value,
  autoComplete,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  minLength,
  maxLength,
  error = false,
  containerOverridesStyle, // for styling complex inputs with dropdowns (e.g., telephone input)
  className = '',
  inputRef,
  inputMode,
  ariaLabelledby,
  ariaDescribedBy,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [inputActive, setInputActive] = useState(Boolean(value));
  const [animateState, setAnimateState] = useState(LabelAnimateState.none);

  useEffect(() => {
    if (!animateLabel) return;
    setAnimateState(
      inputActive ? LabelAnimateState.active : LabelAnimateState.inactive,
    );
  }, [inputActive, animateLabel]);

  useEffect(() => {
    setInputValue(value || '');
    setInputActive(Boolean(value));
  }, [value]);

  const {
    inputtextinputsNoneDisabled: inputStyleDisabled,
    inputtextinputsNoneFocus: inputStyleFocus,
    inputtextinputsNoneNone: inputStyle,
    inputtextinputsNoneError: inputStyleError,
  } = useTheme();

  const inputClasses = clsx({
    [styles.input]: true,
    [styles.centeredStyle]: centered,
    [styles.inputActive]: inputActive && label,
    [styles.disabled]: disabled,
  });

  const containerClasses = clsx({
    [className]: !!className,
    [styles.container]: true,
  });

  const style = {
    ...(disabled ? inputStyleDisabled : inputStyle),
    ...(inputActive ? inputStyleFocus : undefined),
    ...(error ? inputStyleError : undefined),
    ...containerOverridesStyle,
  };

  return (
    <div className={containerClasses}>
      {label && (
        <Label
          htmlFor={id}
          labelState={animateState}
          centered={centered}
          verticalAlign={LabelVerticalAlign.middle}
        >
          {label}
        </Label>
      )}
      <input
        className={inputClasses}
        id={id}
        style={style}
        name={name}
        title={title}
        type={type}
        autoComplete={autoComplete}
        disabled={disabled}
        value={inputValue}
        placeholder={placeholder}
        onChange={(e): void => {
          setInputValue(e.target.value);
          if (onChange) onChange(e);
        }}
        onFocus={(): void => {
          setInputActive(true);
          if (onFocus) onFocus();
        }}
        onBlur={(): void => {
          setInputActive(Boolean(inputValue));
          if (onBlur) onBlur();
        }}
        minLength={minLength}
        maxLength={maxLength}
        ref={inputRef}
        inputMode={inputMode}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedBy}
        aria-invalid={!!error}
      />
    </div>
  );
};

export default TextInput;
